import { Component, DestroyRef, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { catchError, debounceTime, EMPTY, filter, Subscription, switchMap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { CheckInService } from '../../services/check-in.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HumanPipe } from '../pipes/human.pipe';
import { MatInputModule } from '@angular/material/input';
import { BudgetService } from '../../budget/budget.service';
import { InvoiceService } from '../../invoice/invoice.service';
import { DatePipe } from '@angular/common';
import { MoneyPipe } from '../pipes/money.pipe';

@Component({
  selector: 'app-document-select',
  templateUrl: './document-select.component.html',
  styleUrl: './document-select.component.scss',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: DocumentSelectComponent,
    },
  ],
  imports: [
    DatePipe,
    HumanPipe,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MoneyPipe,
    ReactiveFormsModule,
  ]
})
export class DocumentSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  displayWith = input('correlative_id');

  documentType = input<string>();

  labelText = input<string>();

  placeholderText = input<string>();

  prefix = input<string>();

  onload = output<DocumentType>();

  displayDocumentFn = (document) => document?.[this.displayWith()];

  documents = signal([]);
  
  loading = signal<boolean>(false);

  private readonly fb = inject(UntypedFormBuilder);

  form = this.fb.group({
    document_id: [null],
  });


  private readonly budgetService = inject(BudgetService);

  private readonly checkInService = inject(CheckInService);

  private readonly invoiceService = inject(InvoiceService);

  private readonly destroyRef = inject(DestroyRef);

  private subs: Subscription;

  private documentServiceMap = {
    Budget: this.budgetService,
    Order: this.checkInService,
    'Invoice::Sale': this.invoiceService,
  }

  get service() {
    return this.documentServiceMap[this.documentType()];
  }

  onChange = (document_id: number) => {};

  onTouched = () => {};

  ngOnInit() {
    this.setDocumentFormHandler();
  }

  writeValue(documentId: number) {
    this.form.get('document_id').setValue(documentId);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  private setDocumentFormHandler() {
    this.subs = this.form.get('document_id').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      filter((value) => {
        if (!value) {
          this.onChange(null);
          this.onload.emit(null);
          return false;
        }

        if (typeof value !== 'object') {
          this.onChange(null);
          this.onload.emit(null);
          return true;
        }

        this.loadDocument(value.id);
        this.onChange(value.id);
        return false;
      }),
      switchMap((query) => {
        this.loading.set(true);
        return this.service?.search({ query, checked_in: true, completed: 'false' });
      }),
      catchError((error) => {
        console.error(`Error searching document:`, error);
        this.loading.set(false);
        return EMPTY;
      }),
    ).subscribe((sourceDocuments: HttpResponse<any>) => {
      this.documents.set(sourceDocuments.body.items);
      this.loading.set(false);
    });
  }


  loadDocument(id: number): void {
    if (!id) return;

    this.loading.set(true);
    this.service.get(id).pipe(
      catchError((error) => {
        console.error(`Error loading document:`, error);
        this.loading.set(false);
        return EMPTY;
      }),
    ).subscribe((document: HttpResponse<DocumentType> | DocumentType) => {
      this.loading.set(false);
      if (!document) return;

      if (document["body"]) {
        this.onload.emit((document as HttpResponse<DocumentType>).body);
      } else {
        this.onload.emit(document as DocumentType);
      }
    });
  }

  protected onCreate(document) {
    if (!document) return;

    this.documents.update(documents => [document, ...documents]);
    this.form.get('document_id').setValue(document);
  }

  ngOnDestroy() {
    if (this.subs) {
      this.subs.unsubscribe();
    }
  }
}
